import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { UploadFile } from "@mui/icons-material";
import { ADD_REFERENCE, UPDATE_REFERENCE } from "../../../../Apis/order_api";
import { ToastShow } from "../../../../Component/Toast/Toast";

// Same rules as the API (orders/addReference).
const IMAGE_EXT = ["jpg", "jpeg", "png", "heic", "webp"];
const DOC_EXT = ["pdf", "doc", "docx"];
const AUDIO_EXT = ["mp3", "wav", "ogg", "m4a", "aac", "opus"];
const MAX_BYTES = 50 * 1024 * 1024;

const TYPES = [
  { value: 1, label: "Text" },
  { value: 2, label: "Link" },
  { value: 3, label: "Image" },
  { value: 4, label: "Document" },
  { value: 5, label: "Audio" },
];

const EXT_BY_TYPE = { 3: IMAGE_EXT, 4: DOC_EXT, 5: AUDIO_EXT };
const CHOOSE_LABEL = { 3: "Choose images", 4: "Choose document", 5: "Choose audio" };

const extOf = (name) => String(name).split(".").pop().toLowerCase();

/**
 * Admin: add a reference to an order, or edit one (`editing`).
 * Image: several files can be chosen, uploaded one by one (the API takes one file per call).
 * Audio: upload a file (mp3, wav, ogg, m4a, aac, opus); recording is only in the user app.
 * Edit: change text / link / note, or replace the file. The type can't change.
 */
const AddReferenceDialog = ({ ordersId, editing, onClose }) => {
  const isEdit = Boolean(editing);
  const [type, setType] = useState(editing?.type ?? 1);
  const [description, setDescription] = useState(editing?.description ?? "");
  const [link, setLink] = useState(editing?.link ?? "");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState("");

  const isFileType = type >= 3 && type <= 5;
  const allowed = EXT_BY_TYPE[type] ?? [];

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const ok = [];
    for (const f of picked) {
      if (!allowed.includes(extOf(f.name))) {
        ToastShow(`Only ${allowed.join(", ")} files are allowed.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        ToastShow(`${f.name} is larger than 50 MB.`);
        continue;
      }
      ok.push(f);
    }
    setFiles(ok);
  };

  const validate = () => {
    if (type === 1 && !description.trim()) return "Please enter the reference text.";
    if (type === 2 && !/^https?:\/\/\S+$/i.test(link.trim()))
      return "Please enter a valid link starting with http:// or https://";
    if (isFileType && !isEdit && files.length === 0) return "Please select a file.";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      ToastShow(err);
      return;
    }

    if (isEdit) {
      setBusy("Saving…");
      const res = await UPDATE_REFERENCE({
        id: editing.id,
        description: description.trim(),
        link: type === 2 ? link.trim() : undefined,
        file: isFileType ? files[0] : undefined,
      });
      setBusy("");
      if (res && res.s == 1) {
        ToastShow("Reference updated");
        onClose(true);
      } else {
        ToastShow(res?.m ?? "Opps! something went wrong. Please try again.");
      }
      return;
    }

    // Add: one call per file (or one call for text / link).
    const items = isFileType ? files : [null];
    let done = 0;
    for (let i = 0; i < items.length; i++) {
      setBusy(items.length > 1 ? `Uploading ${i + 1} of ${items.length}…` : "Saving…");
      const res = await ADD_REFERENCE({
        ordersId,
        type,
        description: description.trim(),
        link: type === 2 ? link.trim() : undefined,
        file: items[i] ?? undefined,
      });
      if (!res || res.s != 1) {
        ToastShow(res?.m ?? "Opps! something went wrong. Please try again.");
        break; // e.g. the 20-reference limit
      }
      done++;
    }
    setBusy("");
    if (done > 0) {
      ToastShow(done > 1 ? `${done} references added` : "Reference added");
      onClose(true);
    }
  };

  return (
    <Dialog open onClose={() => !busy && onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: "#454545" }}>
        {isEdit ? "Edit reference" : "Add reference"}
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          select
          size="small"
          label="Type"
          value={type}
          disabled={isEdit}
          onChange={(e) => {
            setType(Number(e.target.value));
            setFiles([]);
          }}
        >
          {TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>

        {type === 2 ? (
          <TextField
            size="small"
            label="Link (https://…)"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
        ) : null}

        {isFileType ? (
          <Box>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFile />}
              sx={{ textTransform: "none" }}
            >
              {isEdit ? "Replace file" : CHOOSE_LABEL[type]}
              <input
                hidden
                type="file"
                multiple={!isEdit && type === 3}
                accept={allowed.map((x) => "." + x).join(",")}
                onChange={pickFiles}
              />
            </Button>
            <Typography fontSize="12px" color="#7A7A7A" mt={1}>
              {files.length
                ? files.map((f) => f.name).join(", ")
                : isEdit
                  ? `Current: ${editing.file_name || editing.file}`
                  : `${allowed.join(", ")} · max 50 MB${type === 3 ? " · several allowed" : ""}`}
            </Typography>
          </Box>
        ) : null}

        <TextField
          size="small"
          multiline
          minRows={type === 1 ? 3 : 1}
          maxRows={6}
          label={type === 1 ? "Reference text" : "Note (optional)"}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {busy ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: "auto" }}>
            <CircularProgress size={16} />
            <Typography fontSize="12px">{busy}</Typography>
          </Box>
        ) : null}
        <Button onClick={() => onClose(false)} disabled={Boolean(busy)} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={Boolean(busy)}
          sx={{
            textTransform: "none",
            bgcolor: "#F8AF41",
            color: "white",
            "&:hover": { bgcolor: "rgb(173, 123, 44)" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddReferenceDialog;
